/** TanStack Query hooks for course version draft workflow. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import type {
  CourseVersion,
  CourseVersionWithStructure,
  PaginatedResponse,
} from '@formency/shared';

// ─── Query key factory ─────────────────────────────────────────────────────

export const courseVersionKeys = {
  all: (courseId: string) => ['f', 'courseVersions', courseId] as const,
  list: (courseId: string, page: number) =>
    [...courseVersionKeys.all(courseId), 'list', page] as const,
  active: (courseId: string) => [...courseVersionKeys.all(courseId), 'active'] as const,
  detail: (courseId: string, versionId: string) =>
    [...courseVersionKeys.all(courseId), 'detail', versionId] as const,
};

// ─── Queries ───────────────────────────────────────────────────────────────

/** Fetch paginated list of versions for a course. */
export function useCourseVersions(courseId: string | undefined, page = 1, pageSize = 20) {
  return useQuery<PaginatedResponse<CourseVersion>>({
    queryKey: courseVersionKeys.list(courseId!, page),
    queryFn: () =>
      api.get<PaginatedResponse<CourseVersion>>(`/courses/${courseId}/versions`, {
        page,
        pageSize,
      }),
    enabled: !!courseId,
  });
}

/** Fetch the active draft for a course (null if none). */
export function useActiveDraft(courseId: string | undefined) {
  return useQuery<CourseVersionWithStructure | null>({
    queryKey: courseVersionKeys.active(courseId!),
    queryFn: () =>
      api.get<CourseVersionWithStructure | null>(`/courses/${courseId}/versions/active`),
    enabled: !!courseId,
  });
}

/** Fetch a specific course version by ID. */
export function useCourseVersion(courseId: string | undefined, versionId: string | undefined) {
  return useQuery<CourseVersionWithStructure>({
    queryKey: courseVersionKeys.detail(courseId!, versionId!),
    queryFn: () =>
      api.get<CourseVersionWithStructure>(`/courses/${courseId}/versions/${versionId}`),
    enabled: !!courseId && !!versionId,
  });
}

// ─── Mutations ─────────────────────────────────────────────────────────────

/** Create a new draft by forking the live hierarchy. */
export function useCreateDraft(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<CourseVersionWithStructure>(`/courses/${courseId}/versions`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
    },
  });
}

/** Discard an existing draft. */
export function useDiscardDraft(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      api.post(`/courses/${courseId}/versions/${versionId}/discard`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
    },
  });
}

/** Submit a draft for review. */
export function useSubmitForReview(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (versionId: string) =>
      api.post(`/courses/${courseId}/versions/${versionId}/submit`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
    },
  });
}

/** Approve a draft (CM+ only). */
export function useApproveVersion(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, notes }: { versionId: string; notes?: string }) =>
      api.post(`/courses/${courseId}/versions/${versionId}/approve`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
    },
  });
}

/** Request changes on a draft (CM+ only). */
export function useRequestChanges(courseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ versionId, notes }: { versionId: string; notes: string }) =>
      api.post(`/courses/${courseId}/versions/${versionId}/request-changes`, { notes }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: courseVersionKeys.all(courseId) });
    },
  });
}
